import { useState } from 'react';
import { StreamChat } from 'stream-chat';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import * as Sentry from '@sentry/browser';

const useChatClient = () => {
  const { session, signOut } = useAuth();
  const [client, setClient] = useState(null);
  const [channel, setChannel] = useState(null);

  const connectChat = async () => {
    try {
      const userEmail = session?.user?.email;
      if (!userEmail) {
        console.error('No user session available for chat connection.');
        return;
      }

      console.log('Connecting customer support chat for:', userEmail);
      
      const response = await fetch('/api/customerSupport', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ email: userEmail }),
      });

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          console.warn('Authentication error in customer support chat, signing out user');
          try {
            await signOut();
            return;
          } catch (signOutError) {
            console.error('Error signing out user:', signOutError);
          }
        }
        const errorText = await response.text();
        console.error('Failed to fetch customer support data:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const { token, channelId, userId, STREAM_API_KEY } = await response.json();
      console.log('Customer support connection data received');

      const streamClient = StreamChat.getInstance(STREAM_API_KEY);
      await streamClient.connectUser(
        { id: userId, name: userEmail },
        token
      );

      const streamChannel = streamClient.channel('messaging', channelId);
      await streamChannel.watch();

      setClient(streamClient);
      setChannel(streamChannel);
      
      console.log('Customer support chat connected successfully');
    } catch (error) {
      console.error('Error initializing chat:', error);
      Sentry.captureException(error, {
        extra: {
          userEmail: session?.user?.email,
          errorMessage: error.message
        }
      });
    }
  };

  const disconnectChat = async () => {
    try {
      if (client) {
        console.log('Disconnecting customer support chat');
        await client.disconnectUser();
        setClient(null);
        setChannel(null);
      }
    } catch (error) {
      console.error('Error disconnecting chat:', error);
      Sentry.captureException(error);
    }
  };

  return { client, channel, connectChat, disconnectChat };
};

export default useChatClient;